﻿import { Action, ActionErrorCanceled, View, Util } from './config/app.js'
import { formTrackValidity } from './helper.js'

/*
 * Modal
 *	 options
 *		bool formTrackValidity = true
 *		bool closeOnSuccess = true
 *		bool closeOnFail = false
 *		callback onInit(Modal)
 *		callback bool onOpen(Modal, data)
 *		callback onClose(Modal, Event)
 *		callback onActionBefore(Modal, Event)
 *		callback onActionAfter(Modal, Event)
 *		[string actionOpenName]
 *		[string actionSubmitName]
 * @property name
 * @property view
 * @method show()
 * @method hide()
 */
class Modal
{
	constructor( name, options )
	{
		const optionsDefault = {
			actionOpenName: null, // Modal open trigger action name
			actionSubmitName: null, // Modal submit action name
			formTrackValidity: true,
			closeOnSuccess: true, // Modal will close after action succeeds
			closeOnFail: false, // Modal will close after action fails
			closeOnCanceled: false, // Modal will close after action id canceled
			onInit: null, // (modal) Callback for after the modal is initialized
			onOpen: null, // (modal, data)
			onClose: null, // (modal, event)
			onActionBefore: null, // (modal, event) 
			onActionAfter: null, // (modal, event) 
		}

		this.actionOpen = null
		this.actionSubmit = null
		this.name = name
		this.options = {}
		Util.deepAssign(this.options, optionsDefault)
		Util.deepAssign(this.options, options)
		this.view = this.options.view
		delete this.options.view
		this._ready = false

		this.open = this.open.bind(this)
		this.close = this.close.bind(this)

		this.element = document.querySelector('[ui-modal="' + name + '"]')

		if (!this.element)
			throw new Error('Modal element not found: ' + name)

		this.element.setAttribute('role', 'dialog')
		this.element.setAttribute('tabindex', '-1')
		this.element.classList.add('modal', 'fade')

		if (!this.view)
			this.view = View.create({ name: this.element.getAttribute('ui-view') || 'modal/' + name.toLowerCase() })
		this.element.setAttribute('ui-view', this.view.name)

		if (!this.options.actionOpenName)
			this.options.actionOpenName = (name + ' modal').toLowerCase()

		this.actionOpen = Action.create({
			name: this.options.actionOpenName,
			handler: this.open
		})

		if (!this.options.actionSubmitName)
		{
			this.options.actionSubmitName = this.element.getAttribute('ui-action') || (name + ' modal submit').toLowerCase()
			this.element.setAttribute('ui-action', this.options.actionSubmitName)
		}
	}

	async init()
	{
		if (this._ready)
			return

		await this.view.render()

		if (!this.actionSubmit)
			this.actionSubmit = Action.cache(this.options.actionSubmitName)

		if (this.options.formTrackValidity && this.element.tagName == 'FORM')
			formTrackValidity(this.element)

		this.element.addEventListener('action.before', e => this._onActionBefore(e))
		this.element.addEventListener('action.after', e => this._onActionAfter(e))
		if (this.options.onClose)
			$(this.element).on('hide.bs.modal', e => this.options.onClose(this, e))

		this._ready = true

		if (this.options.onInit)
			await this.options.onInit(this)
	}

	async open(resolve, reject, data = {})
	{
		await this.init()

		if (!resolve) resolve = () => { }
		if (!reject) reject = () => { }

		let result
		try
		{
			result = this.options.onOpen ? (await this.options.onOpen(this, data)) !== false : true
		}
		catch (e)
		{
			return reject(e)
		}

		if (!result)
			return resolve(new ActionErrorCanceled('Canceled'))

		this.element.reset()
		this.element.classList.remove(...Object.values(Action.options.cssClass))
		this.element.classList.remove(...Object.values(View.options.cssClass))

		let promise = resolve(data)
		if (this.options.actionSubmitName)
			this.element.setAttribute('ui-action', this.options.actionSubmitName)
		$(this.element).modal('show') // Triggers show.bs.modal
		window.setTimeout(() => this.element.querySelector('input:not([type="hidden"])')?.focus(), 500)
		return promise
	}

	close()
	{
		if (!this._ready)
			throw new Error('Cannot hide modal because it is not ready: ' + this.name)
		$(this.element).modal('hide') // Triggers hide.bs.modal -> this.options.onClose
	}

	async _onActionBefore(e)
	{
		if (e.detail.name != this.options.actionSubmitName)
			return false

		if (e.detail.canceled)
			return false

		if (this.options.onActionBefore)
			 return await this.options.onActionBefore(this, e)
	}

	async _onActionAfter(e)
	{
		if (e.detail.name != this.options.actionSubmitName)
			return false

		if ((e.detail.success && this.options.closeOnSuccess) || (!e.detail.success && this.options.closeOnFail))
			this.close()

		if (e.detail.canceled)
			return this.options.closeOnCanceled ? this.close() : false

		if (this.options.onActionAfter)
			return await this.options.onActionAfter(this, e)
	}
}

export { Modal }
